import { SnapshotSource } from "./api/model/snapshots/Snapshot";
import BasicRecord from "./utils/BasicRecord";

export const EXEC_ENV = {
    local: "local",
    development: "development",
    test: "test",
    beta: "beta",
    production: "production"
};

export const DENSITY: BasicRecord<number> = Object.freeze({
    "BASE": 1,
    "MDPI": 1,
    "mdpi": 1,
    "_1x": 1,
    "1x": 1,
    "WEB": 1,
    "web": 1,
    "UNKNOWN": 1,
    "unknown": 1,
    "UNDEFINED": 1,
    "undefined": 1,

    "HDPI": 1.5,
    "hdpi": 1.5,

    "XHDPI": 2,
    "xhdpi": 2,
    "_2x": 2,
    "2x": 2,

    "XXHDPI": 3,
    "xxhdpi": 3,
    "_3x": 3,
    "3x": 3,

    "XXXHDPI": 4,
    "xxxhdpi": 4,
    "_4x": 4,
    "4x": 4
});

export const RESOURCE_TYPE = Object.freeze({
    COLORS: "colors",
    COMPONENTS: "components",
    TEXT_STYLES: "textStyles",
    COMPONENT_PAGES: "componentPages"
});

export enum PLATFORM {
    WEB = "web",
    IOS = "ios",
    ANDROID = "android",
    MACOS = "osx",
    /** @deprecated */
    BASE = "base"
}

export const SnapshotSourceNames: Record<string, string> = {
    [SnapshotSource.Sketch]: "Sketch",
    [SnapshotSource.Photoshop]: "Adobe Photoshop",
    [SnapshotSource.XD]: "Adobe XD",
    [SnapshotSource.Figma]: "Figma"
};

export interface PlatformContext {
    id: PLATFORM;
    name: string;
    dimensionSuffix: string;
    textDimensionSuffix: string;
    items?: string[];
    isDeprecated?: boolean;
}

export const PlatformContextMap: Record<PLATFORM, PlatformContext> = Object.freeze({
    [PLATFORM.WEB]: {
        id: PLATFORM.WEB,
        name: "Web",
        dimensionSuffix: "px",
        textDimensionSuffix: "px",
        items: [
            "• CSS, HTML, Tailwind",
            "• SVG and PNG",
            "• Dimensions in px/rem",
            "• VS Code integration"
        ]
    },
    [PLATFORM.IOS]: {
        id: PLATFORM.IOS,
        name: "iOS",
        dimensionSuffix: "pt",
        textDimensionSuffix: "pt",
        items: [
            "• SwiftUI or UIKit",
            "• PDF or SVG, PNG",
            "• Dimensions in pt",
            "• Xcode integration"
        ]
    },
    [PLATFORM.ANDROID]: {
        id: PLATFORM.ANDROID,
        name: "Android",
        dimensionSuffix: "dp",
        textDimensionSuffix: "sp",
        items: [
            "• XML",
            "• SVG and PNG",
            "• Dimensions in dp/sp",
            "• Android Studio Integration"
        ]
    },
    [PLATFORM.MACOS]: {
        id: PLATFORM.MACOS,
        name: "macOS",
        dimensionSuffix: "pt",
        textDimensionSuffix: "pt",
        items: [
            "• SwiftUI or AppKit",
            "• PDF or SVG, PNG",
            "• Dimensions in pt",
            "• Xcode integration"
        ]
    },
    [PLATFORM.BASE]: {
        id: PLATFORM.BASE,
        name: "Base",
        dimensionSuffix: "px",
        textDimensionSuffix: "px",
        isDeprecated: true
    }
});

export const Keyboard = Object.freeze({
    Key: {
        Meta: "Meta",
        Ctrl: "Control",
        Alt: "Alt",
        Enter: "Enter",
        Escape: "Escape",
        Shift: "Shift",
        g: "g",
        s: "s",
        t: "t",
        v: "v",
        z: "z",
        r: "r",
        p: "p",
        d: "d",
        a: "a",
        b: "b",
        f: "f",
        j: "j",
        k: "k",
        i: "i",
        x: "x",
        Tab: "Tab",
        ArrowUp: "ArrowUp",
        ArrowRight: "ArrowRight",
        ArrowDown: "ArrowDown",
        ArrowLeft: "ArrowLeft",
        Backspace: "Backspace",
        Delete: "Delete",
        Minus: "-",
        Plus: "+",
        Zero: "0",
        Equal: "=",
        Asterisk: "*"
    },
    Code: {
        Minus: "Minus",
        Equal: "Equal",
        Space: "Space",
        Digit0: "Digit0",
        Digit2: "Digit2",
        KeyG: "KeyG"
    }
});

export const Mouse = Object.freeze({
    Button: {
        Left: 0,
        Middle: 1,
        Right: 2
    }
});

// TODO: Not all old usages of these values are migrated to this enum
export enum PageType {
    Workspace = "workspace",
    Activities = "activities",
    Barrel = "barrel"
}

export enum WorkspaceTabs {
    PROJECTS = "projects",
    STYLEGUIDES = "styleguides"
}

export enum DialogTypes {
    UPGRADED_TO_NEW_UI = "upgradedToNewUi",
    TRANSFER = "transferOwnership",
    ARCHIVE = "archive",
    LEAVE = "leave",
    DELETE = "delete",
    SELECT_PARENT = "selectParent",
    REMOVE_PARENT = "removeParent",
    UPGRADE_PLAN = "upgradePlan",
    RESTRICTED_EDITOR_WARNING = "restrictedEditorWarning",
    PREVIEW_PROJECT = "previewProject",
    PREVIEW_STYLEGUIDE = "previewStyleguide",
    PLAN_CHOOSER = "planChooser",
    COPY_BARREL = "copyBarrel",
    ASYNC_COPY_BARREL = "asyncCopyBarrel",
    NOTIFICATION_SETTINGS = "notificationSettings",
    FREE_TRIAL_PROMOTION = "freeTrialPromotion",
    FREE_TRIAL_SIGNUP_COMPLETE = "freeTrialSignupComplete",
    FIGMA_SYNC_IMPORT = "figmaSyncImport",
    FIGMA_SYNC_REDIRECT = "figmaSyncRedirect",
    // TODO-electron-tech-debt-yahya: Move all other dialogs to DialogStore usage
    DeleteScreens = "deleteScreens",
    RenameScreen = "renameScreen",
    DeleteProject = "deleteProject",
    DeleteStyleguide = "deleteStyleguide",
    AssetDownloadTarget = "assetDownloadTarget",
    AndroidResFolderReminder = "androidResFolderReminder",
    StorybookIntegration = "storybookIntegration",
}

export const ProjectViewTypes = Object.freeze({
    DASHBOARD: "dashboard",
    STYLEGUIDE: "styleguide",
    SCREEN: "screen",
    STORYBOOK: "storybook",
    FLOW: "flow",
    COMPONENT: "component"
});

export enum SortTypes {
    NAME = "name",
    CREATED = "created",
    UPDATED = "updated",
    SECTIONS = "sections",
    STATUS_TAGS = "statusTags"
}

export enum AppRoutes {
    Workspace = "Workspace",
    Project = "Project",
    Styleguide = "Styleguide",
    ProjectStyleguide = "ProjectStyleguide",
    Screen = "Screen",
    ScreenVersion = "ScreenVersion",
    Notifications = "Notifications",
    Flow = "Flow",
    ProjectComponent = "ProjectComponent",
    StyleguideComponent = "StyleguideComponent",
    ProjectComponentVersion = "ProjectComponentVersion",
    StyleguideComponentVersion = "StyleguideComponentVersion"
}

export enum BillingPeriod {
    ANNUALLY = "annual",
    MONTHLY = "monthly",
    ETERNAL = "eternal"
}

export enum PLAN_TYPES {
    Project = "Pay per project",
    Workspace = "Pay per seat"
}

export enum WorkspacePlanType {
    TEAM = "team",
    ORGANIZATION = "organization",
    ADVANCED = "advanced"
}

export enum PlanGroup {
    BFF = "bff",
    FREE = "free",
    TEAM = "team",
    ORGANIZATION = "organization",
    ENTERPRISE = "enterprise",
    BASIC = "basic",
    ADVANCED = "advanced"
}

export enum PlanUpgradeTrigger {
    VERSION_LIMIT = "versionLimit",
    VERSION_HISTORY_LIMIT = "versionHistoryLimit",
    USER_LIMIT = "userLimit",
    PROJECT_LIMIT = "projectLimit",
    STYLEGUIDE_LIMIT = "styleguideLimit",
    VARIANT_GROUP_LIMIT = "variantGroupLimit",
    FLOW_PROJECT_LIMIT = "flowDistinctProjectLimit",
    APPROVALS_PROJECT_LIMIT = "approvalsLimit",
    SCREEN_LIMIT = "screenLimit"
}

export enum WorkspaceFreeTrialStatus {
    NOT_USED = "not_used",
    IN_USE = "in_use",
    FINISHED_WITH_PURCHASE = "finished_with_purchase"
}

export enum MainAxisAlignmentTypes {
    "center" = "center",
    "min" = "start",
    "max" = "end",
    "space-between" = "space evenly"
}

export enum CrossAxisAlignmentTypes {
    center = "center",
    min = "start",
    max = "end",
    stretch = "stretch",
    inherit = "mixed"
}

export enum DragDropTargetPosition {
    TOP = "top",
    LEFT = "left",
    RIGHT = "right",
    BOTTOM = "bottom"
}

export enum ZPLProtocol {
    zplDebug = "zpl-debug:",
    zplTest = "zpl-test:",
    zplMelon = "zpl-melon:",
    zplWatermelon = "zpl-watermelon:",
    zplKiwi = "zpl-kiwi:",
    zplBeta = "zpl-beta:",
    zpl = "zpl:"
}

export enum StoryStatus {
    Unlinked = "unlinked",
    Linked = "linked",
    HasIssue = "hasIssue"
}

export enum CouponStatus {
    SUCCESS = "success",
    EXPIRED = "expired",
    MAXED_OUT = "maxedOut",
    NOT_FOUND = "notFound"
}

export enum TagGroupTypes {
    default = "default",
    member = "member",
    status = "status"
}

export enum TagGroupOrderBy {
    NAME = "asc-alpha",
    NONE = "none"
}

export enum ToastVariant {
    Success = "success",
    Information = "information",
    ToggleLock = "toggleLock"
}

export enum DragDropItemType {
    ProjectCard = "ProjectCard",
    ScreenThumbnail = "ScreenThumbnail",
    ProjectSectionsListItem = "ProjectSectionsListItem"
}

export enum ProjectsScrolledState {
    Top = "top",
    Scrolled = "scrolledDown"
}

export const NEW_APPROVAL_CREATED_FEEDBACK_COUNTER_LIMIT = 2;

// for slate editor
export enum MentionElementType {
    Screen = "screenMention",
    User = "userMention"
}
